'use client';

import { AlertTriangle, Clock, Coins, ShieldAlert } from 'lucide-react';

/**
 * Plain-language explainer for Deposit Protection, shared by the
 * manage-modal footer and the full dialog so the copy stays in one
 * place. Mirrors Section-9-style wording: when it fires, the keeper
 * fee, and the "still check in" caveat.
 *
 * The dismissal gate mirrors the `TILE_DEPOSIT_NOTICE_KEY` pattern in
 * `tile-deposit-notice-modal.tsx`: a versioned localStorage flag so a
 * buyer only acknowledges once per device, and bumping the suffix
 * re-surfaces materially changed copy. Dismissal NEVER skips the
 * signing prompt — it only collapses the inline explainer.
 */

const PROTECTION_INTRO_KEY = 'takeover-protection-intro-v1';

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function hasAcknowledgedProtectionIntro(): boolean {
  if (!isBrowser()) return false;
  try {
    return window.localStorage.getItem(PROTECTION_INTRO_KEY) === '1';
  } catch {
    return false;
  }
}

export function markProtectionIntroAcknowledged() {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(PROTECTION_INTRO_KEY, '1');
  } catch {
    // ignore quota / privacy-mode errors
  }
}

function Row({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2 text-[11px] leading-relaxed text-zinc-300">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{children}</span>
    </div>
  );
}

export function ProtectionExplainer({
  showMetaMaskNote = false,
  className,
}: {
  /** Show the one-time MetaMask Smart-Account upgrade note (7715 path). */
  showMetaMaskNote?: boolean;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="flex flex-col gap-2">
        <Row icon={<Clock className="h-3.5 w-3.5 text-emerald-300" />}>
          <strong className="font-semibold text-zinc-100">When it kicks in.</strong>{' '}
          If a tile gets close to expiring (under 3 days left), a small top-up keeps it healthy
          for another 7 days.
        </Row>
        <Row icon={<Coins className="h-3.5 w-3.5 text-amber-300" />}>
          <strong className="font-semibold text-zinc-100">1% keeper fee.</strong>{' '}
          Each top-up includes a small 1% fee on top, sent to the Boardroom.
        </Row>
        <Row icon={<AlertTriangle className="h-3.5 w-3.5 text-rose-300" />}>
          <strong className="font-semibold text-zinc-100">Stay involved.</strong>{' '}
          Networks and wallets can fail. Treat this as a safety net — keep checking on your tiles.
        </Row>
        {showMetaMaskNote && (
          <Row icon={<ShieldAlert className="h-3.5 w-3.5 text-amber-300" />}>
            <strong className="font-semibold text-zinc-100">MetaMask: one-time upgrade.</strong>{' '}
            MetaMask will ask you to upgrade this account to a Smart Account the first time. The
            upgrade is permanent and won&rsquo;t roll back if you later disable protection.
          </Row>
        )}
      </div>
    </div>
  );
}
